import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchTicketThreadMessages } from "@/pages/tickets/api/ticketHistoryApi";
import { ticketThreadMerger } from "@/lib/TicketThreadMerger";

/**
 * Loads ticket messages and merges with inbox session thread when both exist.
 *
 * @param {{ selectedTicket: { id: string } | null, inboxMessages: object[], workspaceId: string | null }} params
 */
export function useTicketThreadMessages({ selectedTicket, inboxMessages, workspaceId }) {
  const [ticketRows, setTicketRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const refreshTicketMessages = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!selectedTicket?.id) {
      setTicketRows([]);
      return;
    }
    let cancelled = false;
    // Clear the previous ticket's messages immediately on switch, so a slow/empty
    // fetch can't leave the prior ticket's thread showing on this one.
    setTicketRows([]);
    setLoading(true);
    (async () => {
      try {
        const data = await fetchTicketThreadMessages({
          ticketId: selectedTicket.id,
          workspaceId,
        });
        if (cancelled) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setTicketRows(rows);
      } catch {
        if (!cancelled) setTicketRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTicket?.id, workspaceId, reloadToken]);

  const mergedMessages = useMemo(() => {
    const inbox = Array.isArray(inboxMessages) ? inboxMessages : [];
    return ticketThreadMerger.mergeWithInboxMessages(ticketRows, inbox);
  }, [ticketRows, inboxMessages]);

  return { mergedMessages, ticketLoading: loading, refreshTicketMessages };
}
